import { useRef, useState } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';

/**
 * The FIRST suggestion that starts with `value` (case-insensitive) and is
 * longer than it, or null. Order matters, so callers pass the list in the
 * order they want matched — for the Default Caption Sequence that is its
 * sort order (DV-2859).
 *
 * Accepting a suggestion saves THIS string, in the suggestion's own case
 * (DV-3400). It used to save the typed prefix plus the ghost remainder, so
 * "bed" + Tab became "bedroom" while the sequence entry reads "Bedroom" —
 * an editor had to retype the capital letter the suggestion already had.
 *
 * DV-2859 removed the hardcoded room list that used to live here: six of its
 * names were not on the Default Caption Sequence, so a photo captioned from
 * a suggestion could sink to the bottom on Caption Sort. Suggestions now
 * come from the same list the sort uses (useCaptionSuggestions), and there
 * is no second source to drift.
 */
export function completionFor(
  value: string,
  suggestions: readonly string[]
): string | null {
  if (!value) return null;
  const match = suggestions.find(
    (s) =>
      s.toLowerCase().startsWith(value.toLowerCase()) && s.length > value.length
  );
  return match ?? null;
}

/**
 * What the overlay draws after the typed text: the part of the completion
 * the editor has not typed yet. Display only — acceptance uses the whole
 * completion, never `value + ghost`.
 */
export function ghostFor(
  value: string,
  suggestions: readonly string[]
): string {
  const match = completionFor(value, suggestions);
  return match ? match.slice(value.length) : '';
}

interface UseCaptionEditorArgs {
  initial: string;
  /** Ghost-autocomplete candidates, in match-priority order. */
  suggestions: readonly string[];
  onCommit: (caption: string) => void;
  onNext?: () => void;
  onPrev?: () => void;
}

export function useCaptionEditor({
  initial,
  suggestions,
  onCommit,
  onNext,
  onPrev
}: UseCaptionEditorArgs) {
  const [value, setValue] = useState(initial);
  // Esc must cancel, but the blur it triggers runs against the pre-reset
  // closure — this flag tells that blur to skip committing the stale value.
  const skipBlurCommit = useRef(false);
  const completion = completionFor(value, suggestions);
  const ghost = ghostFor(value, suggestions);

  const commit = (next: string) => {
    if (next !== initial) onCommit(next);
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) =>
    setValue(e.target.value);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const caretAtEnd = e.currentTarget.selectionStart === value.length;
    if (e.key === 'Enter') {
      e.preventDefault();
      skipBlurCommit.current = true;
      // A showing suggestion is accepted on Enter too — the prototype commits
      // `suggestion||draft`, so the whole suggestion is saved, not just what
      // was typed — and in the suggestion's own case (DV-3400).
      const next = completion ?? value;
      setValue(next);
      commit(next);
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      skipBlurCommit.current = true;
      setValue(initial);
      e.currentTarget.blur();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      if (completion) {
        // A showing ghost is always accepted on Tab, regardless of Shift.
        setValue(completion);
      } else {
        commit(value);
        (e.shiftKey ? onPrev : onNext)?.();
      }
    } else if (
      (e.key === 'ArrowRight' || e.key === 'End') &&
      completion &&
      caretAtEnd
    ) {
      e.preventDefault();
      setValue(completion);
    }
  };

  const onBlur = () => {
    if (skipBlurCommit.current) {
      skipBlurCommit.current = false;
      return;
    }
    commit(value);
  };

  return { value, ghost, onChange, onKeyDown, onBlur };
}
